import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

export interface DashboardGenerateRequest {
  folderUuid: string;
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
}

export interface DashboardGenerateResponse {
  success: boolean;
  message: string;
  data: DashboardData;
  monthsProcessed: number;
  processingTime: number;
}

export interface MonthlyIndicator {
  mois: number; // Backend uses French field names
  annee: number;
  // Financial indicators (from Excel)
  masseSalarialeBrute: number | null;
  coutGlobalEmployeur: number | null;
  netAPayer: number | null;
  chargesPatronalesBrutes: number | null;
  chargesPatronalesNettes: number | null;
  chargesSalarialesTotales: number | null;
  heuresSupplementairesTotal: number | null;
  montantHeuresSupplementaires: number | null;
  effectif: number | null;
  // HR indicators (from DSN)
  tauxAbsenteisme: number | null;
  tauxTurnover: number | null;
  nbHommes: number | null;
  nbFemmes: number | null;
  remunerationMoyenneHommes: number | null;
  remunerationMoyenneFemmes: number | null;
  ecartRemunerationPct: number | null;
  nbDeparts: number | null;
  nbArrivees: number | null;
  establishmentCode: string | null;
  establishmentName: string | null;
}

export interface DashboardData {
  uuid: string;
  folderUuid: string;
  folderName: string; // Now comes from backend
  folderCode?: string | null; // Now comes from backend
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
  startPeriod?: string; // Formatted by frontend
  endPeriod?: string; // Formatted by frontend
  monthlyData: MonthlyIndicator[];
  monthlyIndicators?: MonthlyIndicator[]; // Alias for compatibility
  status: string | null;
  errorMessage: string | null;
  // Summary fields calculated by frontend
  summary?: {
    moyenneMasseSalarialeBrute: number;
    moyenneCoutGlobalEmployeur: number;
    moyenneTauxAbsenteisme: number;
    moyenneTauxTurnover: number;
    moyenneEcartRemunerationPct: number;
  };
  previousPeriodSummary?: {
    moyenneMasseSalarialeBrute: number;
    moyenneCoutGlobalEmployeur: number;
    moyenneTauxAbsenteisme: number;
    moyenneTauxTurnover: number;
    moyenneEcartRemunerationPct: number;
  };
  publicShareUrl?: string;
  isPublic?: boolean;
}

export interface DashboardResponse {
  data: DashboardData;
  meta?: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Create axios instance for dashboard-rh service
const dashboardRhApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/dashboard',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(dashboardRhApi);

// Create axios instance for public endpoints (no authentication)
const publicDashboardApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/dashboard',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const dashboardRhService = {
  generateDashboard: async (
    request: DashboardGenerateRequest
  ): Promise<DashboardGenerateResponse> => {
    const response = await dashboardRhApi.post<DashboardGenerateResponse>(
      `/generate`,
      request
    );
    return response.data;
  },

  getDashboard: async (dashboardUuid: string): Promise<DashboardResponse> => {
    const response = await dashboardRhApi.get<{ data: DashboardData }>(
      `/${dashboardUuid}`
    );

    // Transform backend response to match frontend expectations
    const { data } = response.data;

    // Convert string values to numbers in monthlyData
    const monthlyData = data.monthlyData.map(m => ({
      ...m,
      masseSalarialeBrute: toNullableNumber(m.masseSalarialeBrute),
      coutGlobalEmployeur: toNullableNumber(m.coutGlobalEmployeur),
      netAPayer: toNullableNumber(m.netAPayer),
      chargesPatronalesBrutes: toNullableNumber(m.chargesPatronalesBrutes),
      chargesPatronalesNettes: toNullableNumber(m.chargesPatronalesNettes),
      chargesSalarialesTotales: toNullableNumber(m.chargesSalarialesTotales),
      heuresSupplementairesTotal: toNullableNumber(
        m.heuresSupplementairesTotal
      ),
      montantHeuresSupplementaires: toNullableNumber(
        m.montantHeuresSupplementaires
      ),
      effectif: m.effectif
        ? typeof m.effectif === 'string'
          ? parseInt(m.effectif)
          : m.effectif
        : null,
      tauxAbsenteisme: toNullableNumber(m.tauxAbsenteisme),
      tauxTurnover: toNullableNumber(m.tauxTurnover),
      nbHommes: m.nbHommes
        ? typeof m.nbHommes === 'string'
          ? parseInt(m.nbHommes)
          : m.nbHommes
        : null,
      nbFemmes: m.nbFemmes
        ? typeof m.nbFemmes === 'string'
          ? parseInt(m.nbFemmes)
          : m.nbFemmes
        : null,
      remunerationMoyenneHommes: toNullableNumber(m.remunerationMoyenneHommes),
      remunerationMoyenneFemmes: toNullableNumber(m.remunerationMoyenneFemmes),
      ecartRemunerationPct: toNullableNumber(m.ecartRemunerationPct),
      nbDeparts: m.nbDeparts
        ? typeof m.nbDeparts === 'string'
          ? parseInt(m.nbDeparts)
          : m.nbDeparts
        : null,
      nbArrivees: m.nbArrivees
        ? typeof m.nbArrivees === 'string'
          ? parseInt(m.nbArrivees)
          : m.nbArrivees
        : null,
    }));

    // Calculate summary from monthlyData
    const validMonths = monthlyData.filter(m => m.masseSalarialeBrute !== null);
    const summary =
      validMonths.length > 0
        ? {
            moyenneMasseSalarialeBrute:
              validMonths.reduce(
                (sum, m) => sum + (m.masseSalarialeBrute || 0),
                0
              ) / validMonths.length,
            moyenneCoutGlobalEmployeur:
              validMonths.reduce(
                (sum, m) => sum + (m.coutGlobalEmployeur || 0),
                0
              ) / validMonths.length,
            moyenneTauxAbsenteisme:
              validMonths.reduce(
                (sum, m) => sum + (m.tauxAbsenteisme || 0),
                0
              ) / validMonths.length,
            moyenneTauxTurnover:
              validMonths.reduce((sum, m) => sum + (m.tauxTurnover || 0), 0) /
              validMonths.length,
            moyenneEcartRemunerationPct:
              validMonths.reduce(
                (sum, m) => sum + (m.ecartRemunerationPct || 0),
                0
              ) / validMonths.length,
          }
        : {
            moyenneMasseSalarialeBrute: 0,
            moyenneCoutGlobalEmployeur: 0,
            moyenneTauxAbsenteisme: 0,
            moyenneTauxTurnover: 0,
            moyenneEcartRemunerationPct: 0,
          };

    // Add computed fields (folderName now comes from backend)
    data.startPeriod = `${data.startMonth.toString().padStart(2, '0')}/${data.startYear}`;
    data.endPeriod = `${data.endMonth.toString().padStart(2, '0')}/${data.endYear}`;
    data.monthlyIndicators = monthlyData;
    data.monthlyData = monthlyData;
    data.summary = summary;

    return {
      data,
      meta: {
        timestamp: new Date().toISOString(),
        path: `/dashboard/${dashboardUuid}`,
        statusCode: 200,
      },
    };
  },

  shareDashboard: async (
    dashboardUuid: string,
    method: 'public' | 'email',
    emails?: string[]
  ): Promise<{
    success: boolean;
    shareUrl?: string;
    message: string;
    expiresAt?: string;
  }> => {
    const response = await dashboardRhApi.post<{
      data: {
        success: boolean;
        shareUrl?: string;
        message: string;
        expiresAt?: string;
      };
    }>(`/${dashboardUuid}/share`, {
      method,
      emails,
    });
    return response.data.data;
  },

  exportDashboard: async (
    dashboardUuid: string,
    format: 'pdf' | 'word' | 'powerpoint'
  ): Promise<Blob> => {
    const response = await dashboardRhApi.get(`/${dashboardUuid}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  getPublicDashboard: async (
    dashboardUuid: string
  ): Promise<DashboardResponse> => {
    const response = await publicDashboardApi.get<{ data: DashboardData }>(
      `/public/${dashboardUuid}`
    );

    // Transform backend response to match frontend expectations
    const { data } = response.data;

    // Convert string values to numbers in monthlyData
    const monthlyData = data.monthlyData.map(m => ({
      ...m,
      masseSalarialeBrute: toNullableNumber(m.masseSalarialeBrute),
      coutGlobalEmployeur: toNullableNumber(m.coutGlobalEmployeur),
      netAPayer: toNullableNumber(m.netAPayer),
      chargesPatronalesBrutes: toNullableNumber(m.chargesPatronalesBrutes),
      chargesPatronalesNettes: toNullableNumber(m.chargesPatronalesNettes),
      chargesSalarialesTotales: toNullableNumber(m.chargesSalarialesTotales),
      heuresSupplementairesTotal: toNullableNumber(
        m.heuresSupplementairesTotal
      ),
      montantHeuresSupplementaires: toNullableNumber(
        m.montantHeuresSupplementaires
      ),
      effectif: m.effectif
        ? typeof m.effectif === 'string'
          ? parseInt(m.effectif)
          : m.effectif
        : null,
      tauxAbsenteisme: toNullableNumber(m.tauxAbsenteisme),
      tauxTurnover: toNullableNumber(m.tauxTurnover),
      nbHommes: m.nbHommes
        ? typeof m.nbHommes === 'string'
          ? parseInt(m.nbHommes)
          : m.nbHommes
        : null,
      nbFemmes: m.nbFemmes
        ? typeof m.nbFemmes === 'string'
          ? parseInt(m.nbFemmes)
          : m.nbFemmes
        : null,
      remunerationMoyenneHommes: toNullableNumber(m.remunerationMoyenneHommes),
      remunerationMoyenneFemmes: toNullableNumber(m.remunerationMoyenneFemmes),
      ecartRemunerationPct: toNullableNumber(m.ecartRemunerationPct),
      nbDeparts: m.nbDeparts
        ? typeof m.nbDeparts === 'string'
          ? parseInt(m.nbDeparts)
          : m.nbDeparts
        : null,
      nbArrivees: m.nbArrivees
        ? typeof m.nbArrivees === 'string'
          ? parseInt(m.nbArrivees)
          : m.nbArrivees
        : null,
    }));

    // Calculate summary from monthlyData
    const validMonths = monthlyData.filter(m => m.masseSalarialeBrute !== null);
    const summary =
      validMonths.length > 0
        ? {
            moyenneMasseSalarialeBrute:
              validMonths.reduce(
                (sum, m) => sum + (m.masseSalarialeBrute || 0),
                0
              ) / validMonths.length,
            moyenneCoutGlobalEmployeur:
              validMonths.reduce(
                (sum, m) => sum + (m.coutGlobalEmployeur || 0),
                0
              ) / validMonths.length,
            moyenneTauxAbsenteisme:
              validMonths.reduce(
                (sum, m) => sum + (m.tauxAbsenteisme || 0),
                0
              ) / validMonths.length,
            moyenneTauxTurnover:
              validMonths.reduce((sum, m) => sum + (m.tauxTurnover || 0), 0) /
              validMonths.length,
            moyenneEcartRemunerationPct:
              validMonths.reduce(
                (sum, m) => sum + (m.ecartRemunerationPct || 0),
                0
              ) / validMonths.length,
          }
        : {
            moyenneMasseSalarialeBrute: 0,
            moyenneCoutGlobalEmployeur: 0,
            moyenneTauxAbsenteisme: 0,
            moyenneTauxTurnover: 0,
            moyenneEcartRemunerationPct: 0,
          };

    // Add computed fields
    data.startPeriod = `${data.startMonth.toString().padStart(2, '0')}/${data.startYear}`;
    data.endPeriod = `${data.endMonth.toString().padStart(2, '0')}/${data.endYear}`;
    data.monthlyIndicators = monthlyData;
    data.monthlyData = monthlyData;
    data.summary = summary;

    return {
      data,
      meta: {
        timestamp: new Date().toISOString(),
        path: `/public/dashboard/${dashboardUuid}`,
        statusCode: 200,
      },
    };
  },
};
